import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { useCompanyBySlug } from '../hooks/useCompanyBySlug';
import { useSettings } from '../hooks/useSettings';
import { useCompanyPageSEO } from '../hooks/useCompanyPageSEO';
import { PLATFORM_HOSTNAMES } from '../config/platformHostnames';
import LanguageToggle from '../components/LanguageToggle';
import ContactSection from '../components/ContactSection';
import PublicFooter from '../components/public/PublicFooter';
import BrandDefaultIcon from '../components/BrandDefaultIcon';
import { getReadableTextColor } from '../components/public/HeroSection';

/**
 * Pagina contatti pubblica per ogni singola azienda cliente.
 *
 * Rotta: /:companySlug/contatti
 *
 * In multi-tenant, ogni azienda ha la propria pagina contatti, accessibile
 * direttamente da /:companySlug/contatti.
 *
 * Lo slug viene risolto in companyId tramite useCompanyBySlug (stesso hook usato
 * da PublicCarList), e i settings reali vengono letti da
 * companies/{companyId}/settings tramite useSettings(companyId).
 *
 * FIX: in precedenza questa pagina chiamava useSettings() senza companyId,
 * quindi mostrava sempre i valori di default (vuoti) invece dei dati reali
 * dell'azienda — i contatti non comparivano mai qui.
 */
const ContactPage = ({ companySlug: propSlug }) => {
  const { companySlug: paramSlug } = useParams();
  const companySlug = propSlug || paramSlug;

  // Su dominio personalizzato il routing è "pulito" (niente slug in URL —
  // vedi CustomDomainSite in App.js), quindi il link Home deve puntare a "/",
  // non a "/${companySlug}" (stesso pattern usato in PublicCarList.js).
  const isCustomDomain =
    typeof window !== 'undefined' && !PLATFORM_HOSTNAMES.has(window.location.hostname);
  const basePath = isCustomDomain ? '' : `/${companySlug}`;

  const { companyId, ragioneSociale, loading: companyLoading, notFound } =
    useCompanyBySlug(companySlug);

  const { settings, loading: settingsLoading } = useSettings(companyId);

  const si = settings?.sito || {};
  const az = settings?.azienda || {};
  // Indirizzo completo per il footer legale (obbligo D.Lgs. 70/2003 art. 7).
  const indirizzoFooter = [az.indirizzo, [az.cap, az.citta].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const accentColor = si.coloreAccento || '#2563EB';
  const secondaryColor = si.coloreSecondario || accentColor;
  const secondaryTextColor = getReadableTextColor(secondaryColor);
  const nomeAzienda = si.nomeSito || ragioneSociale || companySlug;

  // SEO: prima questa pagina ereditava title e canonical della home piattaforma.
  useCompanyPageSEO({
    enabled: !companyLoading && (notFound || !!(si.nomeSito || ragioneSociale)),
    noindex: !!notFound,
    title: notFound ? 'Autonoleggio non trovato' : `Contatti — ${nomeAzienda}`,
    description: `Contatta ${nomeAzienda}: telefono, email, indirizzo e orari per informazioni e prenotazioni.`,
  });

  if (companyLoading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F6FA' }}>
        <div className="text-center">
          <div
            className="inline-block w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mb-4"
            style={{ borderColor: accentColor, borderTopColor: 'transparent' }}
          />
          <p className="text-slate-500">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F5F6FA' }}>
        <div className="text-center max-w-md">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-800 mb-2">Autonoleggio non trovato</h1>
          <p className="text-slate-500 mb-6">
            L'indirizzo che hai visitato non corrisponde a nessun autonoleggio registrato su NoleggioinCloud.
          </p>
          <a href="/" className="text-blue-600 font-semibold hover:underline" style={{ textDecoration: 'none' }}>
            ← Torna a NoleggioinCloud
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F6FA', '--brand': accentColor, '--brand-secondary': secondaryColor }}>

      {/* Header — stesso stile della home (sticky, slate, font-black) per coerenza visiva */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex justify-between items-center">
          <Link to={basePath || '/'} className="flex items-center gap-2 flex-shrink-0" style={{ textDecoration: 'none' }}>
            {(si.logoSito || az.logoAzienda) ? (
              <img
                src={si.logoSito || az.logoAzienda}
                alt={nomeAzienda}
                className="h-7 sm:h-8 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <BrandDefaultIcon className="text-[var(--brand,#2563EB)]" />
            )}
            <span className="text-base sm:text-lg font-black text-[var(--brand,#2563EB)] tracking-tight leading-none">
              {nomeAzienda}
            </span>
          </Link>

          <div className="hidden sm:flex items-center gap-3">
            <LanguageToggle />
            <div className="w-px h-5 bg-slate-200"></div>
            <Link
              to={basePath || '/'}
              className="flex items-center gap-1.5 px-5 py-2 border font-semibold text-sm hover:bg-slate-50 rounded-full transition"
              style={{ textDecoration: 'none', borderColor: secondaryColor, color: secondaryColor }}
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al catalogo
            </Link>
          </div>

          <div className="flex sm:hidden items-center gap-1.5">
            <LanguageToggle />
            <div className="w-px h-4 bg-slate-200"></div>
            <Link
              to={basePath || '/'}
              className="flex items-center gap-1 px-3 py-2 rounded-full text-xs font-bold"
              style={{ textDecoration: 'none', background: secondaryColor, color: secondaryTextColor }}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Catalogo
            </Link>
          </div>
        </div>
      </div>

      {/* Sezione contatti — riceve i settings già caricati qui sopra */}
      <div className="pt-6 sm:pt-10">
        <ContactSection settings={settings} accentColor={accentColor} secondaryColor={secondaryColor} />
      </div>

      <PublicFooter
        displayName={nomeAzienda}
        slogan={si.slogan}
        logoSito={si.logoSito || az.logoAzienda || ''}
        footerSottotitolo={si.footerSottotitolo}
        accentColor={accentColor}
        basePath={basePath}
        ragioneSociale={az.ragioneSociale}
        piva={az.piva}
        indirizzo={indirizzoFooter}
        email={az.email}
        telefono={az.telefono}
      />

    </div>
  );
};

export default ContactPage;
